import { Box, FolderOpen, Cpu, Skull } from "lucide-react";
import { memo, useState } from "react";
import type { PlayerDropsApiSuccessResp } from "@shared/otherTypes";
import { PlayerDropsLoadingSpinner } from "./PlayerDropsGenericSubcards";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DrilldownChangesSubcard from "./DrilldownChangesSubcard";
import DrilldownOverviewSubcard from "./DrilldownOverviewSubcard";
import { DisplayLodType, DrilldownRangeSelectionType } from "./PlayerDropsPage";
import DrilldownResourcesSubcard from "./DrilldownResourcesSubcard";
import DrilldownCrashesSubcard from "./DrilldownCrashesSubcard";

type DrilldownCardLoadingProps = {
    isError?: boolean;
    tabMode: 'environment' | 'diagnostics';
};

export function DrilldownCardLoading({ isError, tabMode }: DrilldownCardLoadingProps) {
    if (tabMode === 'environment') {
        return (
            <div className="w-full flex flex-col gap-8">
                {/* Period Overview Loading */}
                <div className="flex flex-col">
                    <div className="flex items-center gap-2 border-b border-white/5 pb-2 mb-4 text-scPurple">
                        <FolderOpen className="size-4" />
                        <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Period Overview</h3>
                    </div>
                    <div className="bg-white/[0.01] border border-white/5 rounded-lg p-8 flex justify-center items-center">
                        <PlayerDropsLoadingSpinner isError={isError} />
                    </div>
                </div>

                {/* Environment Changes Loading */}
                <div className="flex flex-col">
                    <div className="flex items-center gap-2 border-b border-white/5 pb-2 mb-4 text-scCyan">
                        <Cpu className="size-4" />
                        <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Environment Changes</h3>
                    </div>
                    <div className="bg-white/[0.01] border border-white/5 rounded-lg p-8 flex justify-center items-center">
                        <PlayerDropsLoadingSpinner isError={isError} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col gap-8">
            {/* Resource Kicks Loading */}
            <div className="flex flex-col">
                <div className="flex items-center gap-2 border-b border-white/5 pb-2 mb-4 text-scPurple">
                    <Box className="size-4" />
                    <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Resource Kicks</h3>
                </div>
                <div className="bg-white/[0.01] border border-white/5 rounded-lg p-8 flex justify-center items-center">
                    <PlayerDropsLoadingSpinner isError={isError} />
                </div>
            </div>

            {/* Crash Reasons Loading */}
            <div className="flex flex-col">
                <div className="flex items-center gap-2 border-b border-white/5 pb-2 mb-4 text-scCyan">
                    <Skull className="size-4" />
                    <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Crash Reasons</h3>
                </div>
                <div className="bg-white/[0.01] border border-white/5 rounded-lg p-8 flex justify-center items-center">
                    <PlayerDropsLoadingSpinner isError={isError} />
                </div>
            </div>
        </div>
    );
}

type DrilldownCardProps = PlayerDropsApiSuccessResp['detailed'] & {
    rangeSelected: DrilldownRangeSelectionType;
    displayLod: DisplayLodType;
    tabMode: 'environment' | 'diagnostics';
};

const DrilldownCardInner = function DrilldownCard({
    windowStart,
    windowEnd,
    windowData,
    rangeSelected,
    displayLod,
    tabMode,
}: DrilldownCardProps) {
    const [crashesTargetLimit, setCrashesTargetLimit] = useState(50);
    const [crashesGroupReasons, setCrashesGroupReasons] = useState(false);

    if (tabMode === 'environment') {
        return (
            <div className="w-full flex flex-col gap-8">
                {/* Period Overview */}
                <div className="flex flex-col">
                    <div className="flex items-center gap-2 border-b border-white/5 pb-2 mb-4 text-scPurple">
                        <FolderOpen className="size-4" />
                        <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Period Overview</h3>
                    </div>
                    <div className="bg-white/[0.01] border border-white/5 rounded-lg p-4">
                        <DrilldownOverviewSubcard dropTypes={windowData.dropTypes} />
                    </div>
                </div>

                {/* Environment Changes */}
                <div className="flex flex-col">
                    <div className="flex items-center gap-2 border-b border-white/5 pb-2 mb-4 text-scCyan">
                        <Cpu className="size-4" />
                        <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Environment Changes</h3>
                    </div>
                    <div className="bg-white/[0.01] border border-white/5 rounded-lg p-4">
                        <DrilldownChangesSubcard changes={windowData.changes} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col gap-8">
            {/* Resource Kicks */}
            <div className="flex flex-col">
                <div className="flex items-center gap-2 border-b border-white/5 pb-2 mb-4 text-scPurple">
                    <Box className="size-4" />
                    <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Resource Kicks</h3>
                </div>
                <div className="bg-white/[0.01] border border-white/5 rounded-lg p-4">
                    <DrilldownResourcesSubcard resKicks={windowData.resKicks} />
                </div>
            </div>

            {/* Crash Reasons */}
            <div className="flex flex-col">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/5 pb-2 mb-4 gap-4">
                    <div className="flex items-center gap-2 text-scCyan">
                        <Skull className="size-4" />
                        <h3 className="font-mono text-xs md:text-sm font-bold tracking-wider uppercase">Crash Reasons</h3>
                    </div>
                    
                    <div className="flex gap-2 self-start sm:self-center">
                        <Select
                            value={crashesTargetLimit.toString()}
                            onValueChange={(value) => setCrashesTargetLimit(parseInt(value))}
                        >
                            <SelectTrigger
                                className="w-28 h-7 px-3 text-xs bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-200"
                            >
                                <SelectValue placeholder="Show limit" />
                            </SelectTrigger>
                            <SelectContent className="px-0 bg-card border-white/10 text-white">
                                <SelectItem value={'50'} className="cursor-pointer hover:bg-white/5">
                                    Top 50
                                </SelectItem>
                                <SelectItem value={'100'} className="cursor-pointer hover:bg-white/5">
                                    Top 100
                                </SelectItem>
                                <SelectItem value={'0'} className="cursor-pointer hover:bg-white/5">
                                    Show All
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        <Select
                            value={crashesGroupReasons.toString()}
                            onValueChange={(value) => setCrashesGroupReasons(value === 'true')}
                        >
                            <SelectTrigger
                                className="w-32 h-7 px-3 text-xs bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-200"
                            >
                                <SelectValue placeholder="Grouping" />
                            </SelectTrigger>
                            <SelectContent className="px-0 bg-card border-white/10 text-white">
                                <SelectItem value={'false'} className="cursor-pointer hover:bg-white/5">
                                    Sort by Count
                                </SelectItem>
                                <SelectItem value={'true'} className="cursor-pointer hover:bg-white/5">
                                    Group Reasons
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="bg-white/[0.01] border border-white/5 rounded-lg p-4">
                    <DrilldownCrashesSubcard
                        crashTypes={windowData.crashTypes}
                        crashesGroupReasons={crashesGroupReasons}
                        crashesTargetLimit={crashesTargetLimit}
                        setCrashesTargetLimit={setCrashesTargetLimit}
                    />
                </div>
            </div>
        </div>
    );
};

export default memo(DrilldownCardInner);
